// AnimePage - Профессиональная версия с полной интеграцией бэкенд API
import React, { useState, useEffect } from 'react'
import styles from './AnimePage.module.scss'
import WelcomeHeader from '../../WelcomePage/components/WelcomeHeader/WelcomeHeader'
import { 
  getAnimes, 
  getAnimeGenres, 
  likeAnime,
  getAnimeEpisodes
} from '../../../service/AnimeService'
import Pagination from '../../../components/Pagination/Pagination'
import Loader from '../../../components/Loader/Loader'
import ImageWithFallback from '../../../components/ImageWithFallback/ImageWithFallback'
import AnimePlayerModal from '../../../components/AnimePlayerModal/AnimePlayerModal'
import { AnimeIcon, StarIcon, SearchIcon, ClockIcon, DocumentIcon } from '../../../components/CustomIcons'

const AnimePage = ({ embedded = false, goBack }) => {
  const [animes, setAnimes] = useState([])
  const [genres, setGenres] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [filters, setFilters] = useState({
    search: '',
    genre: '',
    type: '',
    year: ''
  })
  const [sortBy, setSortBy] = useState('-rating_avg')
  const [selectedAnime, setSelectedAnime] = useState(null)
  const [showAnimeModal, setShowAnimeModal] = useState(false)
  const [modalEpisodes, setModalEpisodes] = useState([])
  const [episodesLoading, setEpisodesLoading] = useState(false)
  const [likedAnimes, setLikedAnimes] = useState(new Set())
  const [showPlayerModal, setShowPlayerModal] = useState(false)
  const [playerAnime, setPlayerAnime] = useState(null)
  const [playerEpisodes, setPlayerEpisodes] = useState([])
  const [initialEpisode, setInitialEpisode] = useState(null)

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortBy])

  useEffect(() => {
    // Перезагрузка при изменении фильтров с возвратом на первую страницу
    if (currentPage !== 1) {
      setCurrentPage(1)
    } else {
      loadData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.genre, filters.type, filters.year])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Формируем параметры, исключая пустые значения
      const params = {
        page: currentPage,
        ordering: sortBy
      }
      
      // Добавляем только непустые фильтры
      if (filters.search && filters.search.trim()) {
        params.search = filters.search.trim()
      }
      if (filters.genre) {
        params.genre = filters.genre
      }
      if (filters.type) {
        params.type = filters.type
      }
      if (filters.year) {
        params.year = filters.year
      }

      const [animesResponse, genresResponse] = await Promise.all([
        getAnimes(params),
        getAnimeGenres().catch(() => ({ data: { results: [] } }))
      ])
      
      // Обработка ответа от API
      const animesData = animesResponse?.results || animesResponse?.data?.results || animesResponse?.data || []
      const pages = animesResponse?.total_pages || animesResponse?.data?.total_pages || Math.ceil((animesResponse?.count || animesResponse?.data?.count || 0) / 20) || 1
      const count = animesResponse?.count || animesResponse?.data?.count || animesData.length
      
      setAnimes(animesData)
      setTotalPages(pages)
      setTotalCount(count)
      setGenres(genresResponse?.data?.results || genresResponse?.data || [])
      
      // Извлекаем лайкнутые аниме
      const liked = new Set(animesData.filter(anime => anime.is_liked).map(anime => anime.id))
      setLikedAnimes(liked)
      
    } catch (err) {
      setError(`Ошибка загрузки аниме: ${err.message}`)
      console.error('Error loading anime data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleRetry = () => {
    loadData()
  }

  const handleAnimeClick = (anime) => {
    // Всегда открываем модальное окно с информацией и эпизодами
    setSelectedAnime(anime)
    setShowAnimeModal(true)
    loadModalEpisodes(anime)
  }

  const loadModalEpisodes = async (anime) => {
    setEpisodesLoading(true)
    try {
      const response = await getAnimeEpisodes(anime.slug || anime.id)
      const episodes = response?.data?.results || response?.data || []
      setModalEpisodes(episodes)
    } catch (err) {
      console.error('Error loading episodes:', err)
      setModalEpisodes([])
    } finally {
      setEpisodesLoading(false)
    }
  }

  const handleLikeAnime = async (anime, e) => {
    e.stopPropagation()
    try {
      await likeAnime(anime.slug || anime.id)
      
      setLikedAnimes(prev => {
        const newLiked = new Set(prev)
        if (newLiked.has(anime.id)) {
          newLiked.delete(anime.id)
        } else {
          newLiked.add(anime.id)
        }
        return newLiked
      })

      // Обновляем счётчик лайков в списке
      setAnimes(prevAnimes => 
        prevAnimes.map(a => 
          a.id === anime.id 
            ? { ...a, likes_count: likedAnimes.has(anime.id) ? (a.likes_count || 0) - 1 : (a.likes_count || 0) + 1 }
            : a
        )
      )
    } catch (err) {
      console.error('Error toggling like:', err)
    }
  }

  const handleWatchAnime = async (anime, e) => {
    e.stopPropagation()
    
    try {
      // Загружаем все эпизоды для плеера
      const episodesResponse = await getAnimeEpisodes(anime.slug || anime.id)
      const episodes = episodesResponse?.data?.results || episodesResponse?.data || []
      
      if (episodes.length > 0) {
        // Открываем плеер с эпизодами
        setPlayerAnime(anime)
        setPlayerEpisodes(episodes)
        setInitialEpisode(episodes[0])
        setShowPlayerModal(true)
        setShowAnimeModal(false) // Закрываем модалку с деталями
      } else {
        // Если эпизодов нет, показываем информацию
        alert('Для этого аниме пока нет доступных эпизодов')
      }
    } catch (err) {
      console.error('Error loading episodes:', err)
      alert('Ошибка загрузки эпизодов')
    }
  }

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }))
    // Автоматически переходим на первую страницу при изменении фильтра
    if (currentPage !== 1) {
      setCurrentPage(1)
    }
  }

  const handleSearch = (e) => {
    if (e.key === 'Enter' || e.type === 'click') {
      // Автоматически переходим на первую страницу при поиске
      if (currentPage !== 1) {
        setCurrentPage(1)
      } else {
        loadData()
      }
    }
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const getAnimeTypeLabel = (type) => {
    const types = {
      'tv': 'ТВ',
      'movie': 'Фильм',
      'ova': 'OVA',
      'ona': 'ONA',
      'special': 'Спешл'
    }
    return types[type] || type
  }

  const formatYear = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).getFullYear()
    } catch {
      return 'N/A'
    }
  }

  if (loading && animes.length === 0) {
    return (
      <div className={styles.page}>
        {!embedded && <WelcomeHeader />}
        <div className={styles.container}>
          <div className={styles.loadingContainer}>
            <Loader size="large" />
            <p>Загрузка аниме каталога...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      {!embedded && <WelcomeHeader />}
      
      <div className={styles.container}>
        {/* <div className={styles.header}>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>
              <AnimeIcon size={32} style={{ verticalAlign: 'middle', marginRight: '10px' }} /> Аниме
            </h1>
            <p className={styles.subtitle}>Каталог аниме и манги</p>
          </div>
        </div> */}

        {/* Поиск и фильтры */}
        <div className={styles.controls}>
          <div className={styles.topControls}>
            <div className={styles.searchContainer}>
              <input
                type="text"
                placeholder="Поиск аниме по названию..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                onKeyPress={handleSearch}
                className={styles.searchInput}
              />
              <button onClick={handleSearch} className={styles.searchButton}>
                <SearchIcon size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />Найти
              </button>
            </div>

            <div className={styles.statsInfo}>
              Найдено: <strong>{totalCount}</strong> {totalCount === 1 ? 'аниме' : 'аниме'}
            </div>
          </div>

          <div className={styles.filters}>
            <div className={styles.filterGroup}>
              <label>Жанр:</label>
              <select
                value={filters.genre}
                onChange={(e) => handleFilterChange('genre', e.target.value)}
                className={styles.filterSelect}
              >
                <option value="">Все жанры</option>
                {genres.map(genre => (
                  <option key={genre.id} value={genre.id}>
                    {genre.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label>Тип:</label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className={styles.filterSelect}
              >
                <option value="">Все типы</option>
                <option value="tv">ТВ сериал</option>
                <option value="movie">Фильм</option>
                <option value="ova">OVA</option>
                <option value="ona">ONA</option>
                <option value="special">Спецвыпуск</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label>Год:</label>
              <select
                value={filters.year}
                onChange={(e) => handleFilterChange('year', e.target.value)}
                className={styles.filterSelect}
              >
                <option value="">Все годы</option>
                {Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label>Сортировка:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="-rating_avg">По рейтингу</option>
                <option value="-created_at">🆕 Недавно добавленные</option>
                <option value="-release_date">По дате выхода</option>
                <option value="name">По названию (А-Я)</option>
                <option value="-name">По названию (Я-А)</option>
                <option value="-episodes_total">📺 По эпизодам</option>
              </select>
            </div>

            <button 
              onClick={() => {
                setFilters({ search: '', genre: '', type: '', year: '' })
                setSortBy('-rating_avg')
              }}
              className={styles.resetButton}
            >
              🔄 Сбросить
            </button>
          </div>
        </div>

        {error && (
          <div className={styles.errorContainer}>
            <div className={styles.errorIcon}><AnimeIcon size={64} /></div>
            <div className={styles.errorContent}>
              <h3 className={styles.errorTitle}>Ошибка загрузки каталога</h3>
              <p className={styles.errorMessage}>{error}</p>
              <button 
                onClick={handleRetry}
                className={styles.retryButton}
                disabled={loading}
              >
                {loading ? 'Загрузка...' : 'Повторить попытку'}
              </button>
            </div>
          </div>
        )}

        {/* Сетка аниме */}
        <div className={styles.animeGrid}>
          {animes.map((anime) => (
            <div 
              key={anime.id} 
              className={styles.animeCard}
              onClick={() => handleAnimeClick(anime)}
            >
              <div className={styles.animePoster}>
                <ImageWithFallback
                  src={anime.poster}
                  alt={anime.name || anime.title}
                  fallbackIcon="🎌"
                  fallbackText="Нет постера"
                  className={styles.posterImage}
                />
                <div className={styles.animeOverlay}>
                  <button 
                    className={styles.playButton}
                    onClick={(e) => handleWatchAnime(anime, e)}
                  >
                    ▶️ Смотреть
                  </button>
                  <button 
                    className={`${styles.likeButton} ${likedAnimes.has(anime.id) ? styles.liked : ''}`}
                    onClick={(e) => handleLikeAnime(anime, e)}
                  >
                    {likedAnimes.has(anime.id) ? '❤️' : '🤍'}
                  </button>
                </div>
                <div className={styles.typeBadge}>
                  {getAnimeTypeLabel(anime.type)}
                </div>
              </div>
              
              <div className={styles.animeInfo}>
                <h3 className={styles.animeTitle}>
                  {anime.name || anime.title}
                </h3>
                
                {anime.original_name && (
                  <p className={styles.originalName}>{anime.original_name}</p>
                )}
                
                <div className={styles.animeDetails}>
                  <span className={styles.animeEpisodes}>
                    📺 {anime.episodes_total || anime.episodes_count || 0} эп.
                  </span>
                  <span className={styles.animeYear}>
                    📅 {formatYear(anime.release_date)}
                  </span>
                </div>
                
                <div className={styles.animeStats}>
                  <div className={styles.animeRating}>
                    <StarIcon size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{anime.rating_avg ? Number(anime.rating_avg).toFixed(1) : 'N/A'}
                  </div>
                  {anime.likes_count !== undefined && anime.likes_count > 0 && (
                    <div className={styles.animeLikes}>
                      ❤️ {anime.likes_count}
                    </div>
                  )}
                  {anime.reviews_count !== undefined && anime.reviews_count > 0 && (
                    <div className={styles.animeReviews}>
                      💬 {anime.reviews_count}
                    </div>
                  )}
                </div>

                {anime.studio && (
                  <div className={styles.animeStudio}>
                    🎬 {anime.studio}
                  </div>
                )}

                {anime.categories && anime.categories.length > 0 && (
                  <div className={styles.animeGenres}>
                    {anime.categories.slice(0, 3).map((category, idx) => (
                      <span key={category.id || idx} className={styles.genreTag}>
                        {category.name || category}
                      </span>
                    ))}
                  </div>
                )}

                {anime.description && (
                  <p className={styles.animeDescription}>
                    {anime.description.length > 100 
                      ? `${anime.description.substring(0, 100)}...` 
                      : anime.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {animes.length === 0 && !loading && !error && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><SearchIcon size={64} /></div>
            <h3>Аниме не найдено</h3>
            <p>Попробуйте изменить параметры поиска или фильтры</p>
          </div>
        )}

        {/* Пагинация */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      {/* Модальное окно с деталями аниме */}
      {showAnimeModal && selectedAnime && (
        <div className={styles.modalOverlay} onClick={() => setShowAnimeModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={() => setShowAnimeModal(false)}>
              ✕
            </button>
            
            <div className={styles.modalBody}>
              <div className={styles.modalPoster}>
                <ImageWithFallback
                  src={selectedAnime.poster}
                  alt={selectedAnime.name || selectedAnime.title}
                  fallbackIcon="🎌"
                  fallbackText="Нет постера"
                  className={styles.posterImage}
                />
              </div>
              
              <div className={styles.modalInfo}>
                <h2 className={styles.modalTitle}>{selectedAnime.name || selectedAnime.title}</h2>
                
                {selectedAnime.original_name && (
                  <p className={styles.modalOriginalName}>{selectedAnime.original_name}</p>
                )}
                
                <div className={styles.modalMeta}>
                  <span className={styles.modalType}>
                    {getAnimeTypeLabel(selectedAnime.type)}
                  </span>
                  <span className={styles.modalEpisodes}>
                    📺 {selectedAnime.episodes_total || 0} эп.
                  </span>
                  <span className={styles.modalYear}>
                    📅 {formatYear(selectedAnime.release_date)}
                  </span>
                  <span className={styles.modalRating}>
                    <StarIcon size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{selectedAnime.rating_avg ? Number(selectedAnime.rating_avg).toFixed(1) : 'N/A'}
                  </span>
                </div>

                {selectedAnime.studio && (
                  <div className={styles.modalStudio}>
                    <strong>Студия:</strong> {selectedAnime.studio}
                  </div>
                )}

                {selectedAnime.categories && selectedAnime.categories.length > 0 && (
                  <div className={styles.modalGenres}>
                    {selectedAnime.categories.map((category, idx) => (
                      <span key={category.id || idx} className={styles.modalGenreTag}>
                        {category.name || category}
                      </span>
                    ))}
                  </div>
                )}

                {selectedAnime.description && (
                  <div className={styles.modalDescription}>
                    <strong>Описание:</strong>
                    <p>{selectedAnime.description}</p>
                  </div>
                )}

                {/* Список эпизодов */}
                {episodesLoading ? (
                  <div className={styles.episodesLoading}>
                    <Loader size="small" />
                    <p>Загрузка эпизодов...</p>
                  </div>
                ) : modalEpisodes.length > 0 ? (
                  <div className={styles.modalEpisodesList}>
                    <h3 className={styles.episodesTitle}>Эпизоды ({modalEpisodes.length})</h3>
                    <div className={styles.episodesGrid}>
                      {modalEpisodes.slice(0, 12).map((episode) => (
                        <div 
                          key={episode.id} 
                          className={styles.episodeCard}
                          onClick={() => {
                            // Открываем плеер с выбранным эпизодом
                            setPlayerAnime(selectedAnime)
                            setPlayerEpisodes(modalEpisodes)
                            setInitialEpisode(episode)
                            setShowPlayerModal(true)
                            setShowAnimeModal(false)
                          }}
                        >
                          <div className={styles.episodeNumber}>{episode.episode_number}</div>
                          {episode.thumbnail && (
                            <ImageWithFallback
                              src={episode.thumbnail}
                              alt={`Эпизод ${episode.episode_number}`}
                              fallbackIcon="🎬"
                              className={styles.episodeThumbnail}
                            />
                          )}
                          <div className={styles.episodeInfo}>
                            <div className={styles.episodeName}>
                              {episode.name || `Эпизод ${episode.episode_number}`}
                            </div>
                            {episode.duration_min && (
                              <div className={styles.episodeDuration}>⏱ {episode.duration_min} мин</div>
                            )}
                          </div>
                          <div className={styles.playOverlay}>▶</div>
                        </div>
                      ))}
                    </div>
                    {modalEpisodes.length > 12 && (
                      <button 
                        className={styles.showAllEpisodes}
                        onClick={() => {
                          // Открываем плеер с первым эпизодом, пользователь увидит все эпизоды в списке
                          if (modalEpisodes.length > 0) {
                            setPlayerAnime(selectedAnime)
                            setPlayerEpisodes(modalEpisodes)
                            setInitialEpisode(modalEpisodes[0])
                            setShowPlayerModal(true)
                            setShowAnimeModal(false)
                          }
                        }}
                      >
                        Показать все {modalEpisodes.length} эпизодов
                      </button>
                    )}
                  </div>
                ) : null}

                <div className={styles.modalActions}>
                  <button 
                    className={styles.modalWatchButton}
                    onClick={() => {
                      if (modalEpisodes.length > 0) {
                        // Открываем плеер с первым эпизодом
                        setPlayerAnime(selectedAnime)
                        setPlayerEpisodes(modalEpisodes)
                        setInitialEpisode(modalEpisodes[0])
                        setShowPlayerModal(true)
                        setShowAnimeModal(false)
                      } else {
                        alert('Для этого аниме пока нет доступных эпизодов')
                      }
                    }}
                  >
                    ▶️ Смотреть
                  </button>
                  
                  <button 
                    className={`${styles.modalLikeButton} ${likedAnimes.has(selectedAnime.id) ? styles.liked : ''}`}
                    onClick={async (e) => {
                      await handleLikeAnime(selectedAnime, e)
                    }}
                  >
                    {likedAnimes.has(selectedAnime.id) ? '❤️ В избранном' : '🤍 В избранное'}
                  </button>
                </div>

                <div className={styles.modalStats}>
                  {selectedAnime.likes_count !== undefined && selectedAnime.likes_count > 0 && (
                    <div>❤️ {selectedAnime.likes_count} лайков</div>
                  )}
                  {selectedAnime.reviews_count !== undefined && selectedAnime.reviews_count > 0 && (
                    <div>💬 {selectedAnime.reviews_count} отзывов</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальный плеер с эпизодами */}
      {showPlayerModal && playerAnime && playerEpisodes.length > 0 && (
        <AnimePlayerModal
          anime={playerAnime}
          episodes={playerEpisodes}
          initialEpisode={initialEpisode}
          onClose={() => setShowPlayerModal(false)}
          onEpisodeChange={(episode) => setInitialEpisode(episode)}
        />
      )}
    </div>
  )
}

export default AnimePage